import clsx from 'clsx';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useButtonSizeSmaller } from '../../utils/buttonSizes';
import Button from '../../components/Button';
import ChevronIcon from '../../assets/icons/chevron-down-icon.svg?react';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import { toggleSwitch } from '../../reducers/switch';
import Checkbox from '../../components/Checkbox';
import Select from '../../components/Select';
import TextArea from '../../components/TextArea';
import FileInput from '../../components/FileInput';
import {
  GetIncidentByIdResponse,
  IncidentReportingControllerCreateContractorInvolvedEnum,
  IncidentsAffectedBodyPartsEnum,
  IncidentsSeverityEnum,
} from '@EHSNavigator/backend';
import SaveAsDraft from '../../components/SaveAsDraft';
// import DownloadIcon from '../../assets/icons/upload-cloud-line.svg?react';
import CancelIcon from '../../assets/icons/sidebar-cross-icon.svg?react';
import MultiAssigneeDropDown from '../../components/MultiAssigneeDropDown';

interface IncidentReportingStepTwoProps {
  otherClasses?: string;
  attachments?: GetIncidentByIdResponse['incident']['attachments'];
  formHandler: any;
  isDraftLoading: boolean;
  handleSaveAsDraft: () => void;
  handleChangeAttachments: (updatedArray: any) => void;
  handleSteps: React.Dispatch<number>;
  departments: {
    label: string;
    value: string;
  }[];
  incidentTypes: {
    label: string;
    value: string;
  }[];
  disbaleButtons: boolean;
}

const IncidentReportingStepTwo: React.FC<IncidentReportingStepTwoProps> = ({
  otherClasses,
  formHandler,
  handleSteps,
  isDraftLoading,
  handleSaveAsDraft,
  handleChangeAttachments,
  attachments = [],
  departments,
  incidentTypes,
  disbaleButtons,
}) => {
  const incidentReportingStepTwoClasses = clsx(
    otherClasses,
    'w-full flex flex-col flex-1 gap-8',
  );
  const dispatch = useDispatch();
  const sizes = useButtonSizeSmaller();

  const handleRemoveFile = (fileToRemove: any) => {
    const updatedFiles =
      attachments.filter((file) => file.name !== fileToRemove.name) || [];
    handleChangeAttachments(updatedFiles);
  };

  const totalFiles = [...attachments, ...(formHandler?.values?.files || [])];
  const filesError = formHandler?.errors?.files;

  const errorMessage = Array.isArray(filesError)
    ? filesError.some(
        (error: any) => error?.includes('5MB') || error?.includes('50MB'),
      ) && filesError
    : typeof filesError === 'string' && totalFiles?.length > 3
      ? filesError
      : '';

  return (
    <div
      className={incidentReportingStepTwoClasses}
      data-testid="incident-reporting-step-two"
    >
      <div className="flex flex-col lg:gap-10 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex max-w-818 flex-1 items-start gap-2.5">
          <button
            className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
            onClick={() => dispatch(toggleSwitch())}
          >
            <ToggleIcon />
          </button>
          <div>
            <p className="font-inter-variable text-brand-byzantine -tracking-form-label mb-1 text-xs leading-4 font-bold lg:hidden">
              Report an Incident
            </p>
            <h1 className="h3 mb-1 lg:mb-0">What Exactly Happened?</h1>
            <p className="text-brand-byzantine font-inter-variable -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
              STEP 2 / 3
            </p>
            <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
              If you have encountered an incident, please report it here for
              quick and timely resolution of the issue.
            </p>
          </div>
        </div>
        <SaveAsDraft
          handleSaveAsDraft={handleSaveAsDraft}
          isDisabled={isDraftLoading}
          disbaleButtons={disbaleButtons}
        />
      </div>
      <div className="lg:border-t-neutral-tones-text/10 lg:border-t lg:pt-8">
        <div className="mb-8 flex flex-col">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-25 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Department Involved
          </h6>
          <div className="grid w-full max-w-955 grid-cols-1 gap-8 md:grid-cols-2">
            <Select
              label="Select Department*"
              list={departments}
              value={formHandler.values.department}
              disabled={formHandler.isSubmitting}
              placeholder="Departments"
              otherClasses={{ container: 'w-full' }}
              onSelect={(e) =>
                formHandler?.setFieldValue('department', e, true)
              }
              errorMessage={
                formHandler?.touched.department &&
                formHandler?.errors.department
                  ? formHandler?.errors.department
                  : ''
              }
            />
            <Select
              label="Was Contractor Involved?*"
              list={Object.entries(
                IncidentReportingControllerCreateContractorInvolvedEnum,
              ).map(([, value]) => ({
                label: value,
                value: value,
              }))}
              value={formHandler.values.contractorInvolved}
              disabled={formHandler.isSubmitting}
              placeholder="Select Involvement (Y/N)"
              otherClasses={{ container: 'w-full' }}
              onSelect={(e) =>
                formHandler?.setFieldValue('contractorInvolved', e, true)
              }
              errorMessage={
                formHandler?.touched.contractorInvolved &&
                formHandler?.errors.contractorInvolved
                  ? formHandler?.errors.contractorInvolved
                  : ''
              }
            />
          </div>
        </div>
        <div className="mb-8 flex flex-col">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-25 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Incident Type
          </h6>
          <div className="mb-2 grid w-full max-w-955 grid-cols-1 gap-8 md:grid-cols-2">
            <Select
              label="Incident Type*"
              list={incidentTypes}
              value={formHandler.values.incidentType}
              disabled={formHandler.isSubmitting}
              placeholder="Incident Type"
              otherClasses={{ container: 'w-full' }}
              onSelect={(e) => {
                if (
                  incidentTypes?.find(
                    (node) => node.value === formHandler?.values?.incidentType,
                  )?.label === 'Injury/Illness'
                ) {
                  formHandler?.setFieldValue('affectedBodyParts', [], true);
                }
                formHandler?.setFieldValue('incidentType', e, true);
              }}
              errorMessage={
                formHandler?.touched.incidentType &&
                formHandler?.errors.incidentType
                  ? formHandler?.errors.incidentType
                  : ''
              }
            />
            <Select
              label="Incident Severity*"
              list={Object.entries(IncidentsSeverityEnum).map(([, value]) => ({
                label: value,
                value: value,
              }))}
              value={formHandler.values.incidentSeverity}
              disabled={formHandler.isSubmitting}
              placeholder="Incident Severity"
              otherClasses={{ container: 'w-full' }}
              onSelect={(e) =>
                formHandler?.setFieldValue('incidentSeverity', e, true)
              }
              errorMessage={
                formHandler?.touched.incidentSeverity &&
                formHandler?.errors.incidentSeverity
                  ? formHandler?.errors.incidentSeverity
                  : ''
              }
            />
            {incidentTypes?.find(
              (node) => node.value === formHandler?.values?.incidentType,
            )?.label === 'Injury/Illness' && (
              <MultiAssigneeDropDown
                fullLabel={true}
                handleRemove={(e) => {
                  const { affectedBodyParts = [] } = formHandler?.values || {};
                  const updatedAssignees = affectedBodyParts?.filter(
                    (item: any) => item !== e,
                  );
                  formHandler?.setFieldValue(
                    'affectedBodyParts',
                    updatedAssignees,
                    true,
                  );
                }}
                label="Affected Body Parts*"
                list={Object.entries(IncidentsAffectedBodyPartsEnum).map(
                  ([, value]) => ({
                    label: value,
                    value: value,
                  }),
                )}
                value={formHandler.values.affectedBodyParts}
                disabled={formHandler.isSubmitting}
                placeholder="Affected Body Parts"
                otherClasses={{
                  container: 'w-full',
                  button: '!py-0 [&>div]:!py-2',
                  tagClasses:
                    '!w-fit !h-fit !rounded !py-1 !px-2 !min-h-fit !min-w-fit !bg-brand-byzantine !capitalize !text-xs !font-medium [&>button]:!-right-1.5 [&>button]:!-top-1.5',
                }}
                onSelect={(e) => {
                  const { affectedBodyParts = [] } = formHandler?.values || {};
                  const updatedAssignees = affectedBodyParts?.includes(e)
                    ? affectedBodyParts?.filter((item: any) => item !== e)
                    : [...affectedBodyParts, e];

                  formHandler?.setFieldValue(
                    'affectedBodyParts',
                    updatedAssignees,
                    true,
                  );
                }}
                errorMessage={
                  formHandler?.touched.affectedBodyParts &&
                  formHandler?.errors.affectedBodyParts
                    ? formHandler?.errors.affectedBodyParts
                    : ''
                }
              />
            )}
          </div>
          <Checkbox
            checked={formHandler?.values?.multiplePeopleInjured}
            name="multiplePeopleInjured"
            otherClasses="w-fit mt-5"
            onChange={() =>
              formHandler?.setFieldValue(
                'multiplePeopleInjured',
                !formHandler?.values?.multiplePeopleInjured,
                true,
              )
            }
            disabled={false}
            errorMessage=""
            onBlur={formHandler.handleBlur}
            label="Were multiple people injured?"
          />
        </div>
        <div className="flex flex-col">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-25 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Incident Description
          </h6>
          <div className="grid w-full max-w-955 grid-cols-1 gap-25 md:grid-cols-2">
            <TextArea
              name="description"
              onBlur={formHandler?.handleBlur}
              onChange={formHandler?.handleChange}
              value={formHandler?.values?.description}
              placeholder="Write something here e.g. The issue occurred yesterday..."
              disabled={formHandler?.isSubmitting}
              errorMessage={
                formHandler?.touched.description &&
                formHandler?.errors.description
                  ? formHandler?.errors.description
                  : ''
              }
              label="Description*"
              otherClasses={{ container: 'w-full h-fit' }}
            />
            <div className="flex w-full flex-col gap-2">
              <FileInput
                label="Images/Videos (Optional)"
                name="files"
                selectedFiles={formHandler?.values?.files}
                onChange={(e) => {
                  formHandler?.setFieldValue('files', e, true);
                }}
                accept={{
                  'image/png': ['.png'],
                  'image/jpeg': ['.jpg', '.jpeg'],
                  'video/mp4': ['.mp4'],
                  'image/gif': ['.gif'],
                  'video/quicktime': ['.mov'],
                  'video/hevc': ['.hevc'],
                }}
                onBlur={formHandler?.handleBlur}
                errorMessage={errorMessage as string}
              />

              {attachments?.length > 0 && (
                <div className="flex flex-col gap-2">
                  <p className="font-inter-variable text-neutral-tones-text/60 text-sm font-bold">
                    Uploaded Files
                  </p>
                  {attachments?.map((att) => {
                    return (
                      <div
                        key={att.id}
                        className={clsx(
                          'bg-additional-dropdown-select/10 ring-brand-byzantine flex w-full items-center justify-between rounded-md px-15 py-2 ring',
                        )}
                      >
                        <div className="flex items-center gap-2.5">
                          {
                            <img
                              src={att.url}
                              alt={att.name}
                              className="rounded-md-object-fill rounded-5 h-5 w-6"
                            />
                          }
                          <p className="text-form-label text-neutral-tones-text -tracking-form-label line-clamp-1 leading-5 font-bold wrap-anywhere">
                            {att.name}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="cursor-pointer"
                          onClick={() => {
                            handleRemoveFile(att);
                          }}
                        >
                          <CancelIcon className="[&>g>path]:fill-brand-posh-pink h-5 w-5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-8">
        <Button
          onClick={() => handleSteps(1)}
          size={sizes}
          type="button"
          variant="secondary"
          otherClasses="max-w-211 w-full"
          disable={disbaleButtons}
        >
          <ChevronIcon className="h-5 w-5 rotate-90 [&>path]:fill-current" /> Go
          Back
        </Button>
        <Button
          onClick={() => handleSteps(3)}
          size={sizes}
          otherClasses="max-w-211 w-full"
          disable={disbaleButtons}
        >
          Proceed{' '}
          <ChevronIcon className="h-5 w-5 -rotate-90 [&>path]:fill-current" />
        </Button>
      </div>
    </div>
  );
};

export default IncidentReportingStepTwo;
